export type DragDirection = 'left-to-right' | 'right-to-left';

export type VerticalDragIndicatorBehaviour = {
  dragDirection: DragDirection;
  onResize: (width: number) => void;
};

type Drag = { anchor: number; last: number | null; pointerId: number; resting: string; sizer: HTMLElement | null };

const SIZER = '[data-side-panel-sizer]';
const WIDTH = '--side-panel-width';
const HANDLE_CENTRE = 10;
const KEYBOARD_STEP = 16;

export const delegateVerticalDragIndicator = (container: HTMLElement, { dragDirection, onResize }: VerticalDragIndicatorBehaviour) => {
  const body = container.ownerDocument.body;
  const handle = () => container.querySelector<HTMLElement>('.bomdisoft-drag-indicator-handle');
  const bound = (name: 'aria-valuemax' | 'aria-valuemin') => Number(handle()?.getAttribute(name));
  const widest = () => Math.min(bound('aria-valuemax'), body.clientWidth - 100);
  const card = (): HTMLElement | null => container.closest<HTMLElement>('aside') ?? container.closest<HTMLElement>(SIZER);
  const widthAt = (clientX: number, anchor: number) => {
    const width = dragDirection === 'left-to-right' ? clientX - anchor : anchor - clientX;
    return width >= bound('aria-valuemin') && width < widest() ? width : null;
  };
  let drag: Drag | null = null;

  const follow = (clientY: number) => {
    const track = container.querySelector<HTMLElement>('.bomdisoft-drag-indicator-track');
    if (track) track.style.top = `${clientY - (container.getBoundingClientRect().top + HANDLE_CENTRE)}px`;
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const sizer = container.closest<HTMLElement>(SIZER);
    const box = (card() ?? sizer)?.getBoundingClientRect();
    const anchor = box ? (dragDirection === 'left-to-right' ? box.left : box.right) : dragDirection === 'left-to-right' ? body.offsetLeft : body.clientWidth;
    drag = { anchor, last: null, pointerId: event.pointerId, resting: sizer?.style.getPropertyValue(WIDTH) ?? '', sizer };
    sizer?.setAttribute('data-resizing', '');
    container.setAttribute('data-dragging', '');
    container.setPointerCapture?.(event.pointerId);
    follow(event.clientY);
  };

  const handlePointerMove = (event: PointerEvent) => {
    follow(event.clientY);
    if (!drag || drag.pointerId !== event.pointerId) return;
    const width = widthAt(event.clientX, drag.anchor);
    if (width === null) return;
    drag.last = width;
    drag.sizer?.style.setProperty(WIDTH, `${width}px`);
  };

  const finish = (event: PointerEvent) => {
    const ended = drag;
    if (!ended || ended.pointerId !== event.pointerId) return;
    drag = null;
    container.removeAttribute('data-dragging');
    if (container.hasPointerCapture?.(event.pointerId)) container.releasePointerCapture(event.pointerId);
    if (ended.sizer) {
      if (ended.resting === '') ended.sizer.style.removeProperty(WIDTH);
      else ended.sizer.style.setProperty(WIDTH, ended.resting);
      ended.sizer.removeAttribute('data-resizing');
    }
    if (ended.last !== null) onResize(ended.last);
  };

  const handleFocusIn = (event: FocusEvent) => {
    const box = card()?.getBoundingClientRect();
    if (event.target === handle() && box) handle()?.setAttribute('aria-valuenow', String(Math.round(box.width)));
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const current = card()?.getBoundingClientRect().width;
    if (event.target !== handle() || current === undefined) return;
    const wider = dragDirection === 'left-to-right' ? 'ArrowRight' : 'ArrowLeft';
    const narrower = dragDirection === 'left-to-right' ? 'ArrowLeft' : 'ArrowRight';
    const target = { End: bound('aria-valuemin'), Home: widest(), [narrower]: current - KEYBOARD_STEP, [wider]: current + KEYBOARD_STEP }[event.key];
    if (target === undefined) return;
    event.preventDefault();
    const width = Math.round(Math.min(widest(), Math.max(bound('aria-valuemin'), target)));
    handle()?.setAttribute('aria-valuenow', String(width));
    onResize(width);
  };

  container.addEventListener('pointerdown', handlePointerDown);
  container.addEventListener('pointermove', handlePointerMove);
  container.addEventListener('pointerup', finish);
  container.addEventListener('pointercancel', finish);
  container.addEventListener('focusin', handleFocusIn);
  container.addEventListener('keydown', handleKeyDown);
  return () => {
    container.removeEventListener('pointerdown', handlePointerDown);
    container.removeEventListener('pointermove', handlePointerMove);
    container.removeEventListener('pointerup', finish);
    container.removeEventListener('pointercancel', finish);
    container.removeEventListener('focusin', handleFocusIn);
    container.removeEventListener('keydown', handleKeyDown);
  };
};
